import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Crosshair,
  ListPlus,
  Pencil,
  Share2,
  Smartphone,
  Star,
  Trash2,
} from 'lucide-react';
import {
  COLLECTIONS,
  createStationaryPoint,
  formatCoords,
  isMostUsed,
  isNeverUsed,
  isUsedThisWeek,
  parseStationaryCsv,
  PLAYLISTS,
  pointsToCsv,
  STATIONARY_POINTS,
  type MockPlaylist,
  type StationaryPoint,
} from '../../data/stationary-mock';
import StationaryDialogs, { copyText, type StationaryDialog } from './StationaryDialogs';
import StationaryBoard from './StationaryBoard';
import type { MapStyleName, StationaryMapHandle } from './StationaryMap';
import './stationary.css';

export interface StationaryViewProps {
  /** True while the stationary-points request is in flight. */
  loading?: boolean;
}

type ListFilter = 'all' | 'recent' | 'favourite' | 'never' | 'most';
type ListSort = 'recent' | 'name' | 'times' | 'accuracy';
type MenuSource = 'row' | 'detail' | 'float';
type DetailTab = 'overview' | 'usage' | 'activity' | 'nearby';
type PointLayout = 'list' | 'grid';

interface MenuState {
  pointId: string;
  source: MenuSource;
  top: number;
  left: number;
}

interface ToastItem {
  id: number;
  text: string;
}

const MENU_ACTIONS = [
  { id: 'edit-name', label: 'Edit name & notes', icon: Pencil },
  { id: 'edit-accuracy', label: 'Edit accuracy', icon: Crosshair },
  { id: 'toggle-favourite', label: 'Favourite', icon: Star },
  { id: 'shortcut', label: 'Create shortcut', icon: Smartphone },
  { id: 'remove', label: 'Remove from history', icon: Trash2 },
  { id: 'share', label: 'Share point', icon: Share2 },
  { id: 'add-playlist', label: 'Add to playlist', icon: ListPlus },
] as const;

type MenuActionId = (typeof MENU_ACTIONS)[number]['id'];

function sortPoints(list: StationaryPoint[], sort: ListSort): StationaryPoint[] {
  const next = [...list];
  next.sort((a, b) => {
    if (sort === 'name') return a.name.localeCompare(b.name);
    if (sort === 'times') return b.timesUsed - a.timesUsed || a.name.localeCompare(b.name);
    if (sort === 'accuracy') return a.accuracyMeters - b.accuracyMeters || a.name.localeCompare(b.name);
    const aHours = a.lastUsedHours ?? Number.POSITIVE_INFINITY;
    const bHours = b.lastUsedHours ?? Number.POSITIVE_INFINITY;
    return aHours - bHours || a.name.localeCompare(b.name);
  });
  return next;
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function Skel({ className = '', ...rest }: { className?: string } & React.HTMLAttributes<HTMLSpanElement>) {
  return <span className={`sp-skel ${className}`} {...rest} />;
}

function StationarySkeleton() {
  return (
    <div className="sp sp--skeleton" role="status">
      <span className="visually-hidden">Loading stationary points</span>
      <div className="sp-head" data-testid="skeleton-sp-header">
        <Skel className="sp-skel--line" />
      </div>
      <div className="sp-workspace">
        <div className="sp-card sp-list" data-testid="skeleton-sp-table">
          <Skel className="sp-skel--line" />
          <Skel className="sp-skel--row" />
          <Skel className="sp-skel--row" />
          <Skel className="sp-skel--row" />
          <Skel className="sp-skel--row" />
          <Skel className="sp-skel--row" />
        </div>
        <div className="sp-stage">
          <Skel className="sp-skel--map" data-testid="skeleton-sp-map" />
          <div className="sp-card sp-detail" data-testid="skeleton-sp-detail">
            <Skel className="sp-skel--line" />
            <div className="sp-metrics" data-testid="skeleton-sp-stats">
              <Skel className="sp-skel--stat" />
              <Skel className="sp-skel--stat" />
              <Skel className="sp-skel--stat" />
              <Skel className="sp-skel--stat" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const StationaryView: React.FC<StationaryViewProps> = ({ loading = false }) => {
  const [points, setPoints] = useState<StationaryPoint[]>(STATIONARY_POINTS);
  const [playlists, setPlaylists] = useState<MockPlaylist[]>(PLAYLISTS);
  const [selectedId, setSelectedId] = useState<string | null>('home');
  const [mockingId, setMockingId] = useState<string | null>('home');
  const [filter, setFilter] = useState<ListFilter>('all');
  const [sort, setSort] = useState<ListSort>('recent');
  const [city, setCity] = useState('');
  const [collectionId, setCollectionId] = useState<string | null>(null);
  const [tableQuery, setTableQuery] = useState('');
  const [areaQuery, setAreaQuery] = useState('');
  const [layout, setLayout] = useState<PointLayout>('list');
  const [grouped, setGrouped] = useState(true);
  const [moreFilters, setMoreFilters] = useState(false);
  const [mobilePane, setMobilePane] = useState<'points' | 'details'>('points');
  const [mapStyle, setMapStyle] = useState<MapStyleName>('map');
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [dialog, setDialog] = useState<StationaryDialog | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('overview');
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const mapRef = useRef<StationaryMapHandle>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const toastId = useRef(1);
  const toastTimers = useRef<number[]>([]);

  const pushToast = useCallback((text: string) => {
    const id = toastId.current;
    toastId.current += 1;
    setToasts((current) => [...current, { id, text }]);
    const timer = window.setTimeout(() => {
      setToasts((current) => current.filter((item) => item.id !== id));
    }, 3200);
    toastTimers.current.push(timer);
  }, []);

  useEffect(() => () => {
    toastTimers.current.forEach((timer) => window.clearTimeout(timer));
  }, []);

  const closeDialog = useCallback(() => setDialog(null), []);
  const selectPoint = useCallback((id: string) => {
    setSelectedId(id);
  }, []);

  const cities = useMemo(() => {
    const names = new Set(points.map((point) => point.city));
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [points]);

  const counts = useMemo(() => ({
    total: points.length,
    week: points.filter(isUsedThisWeek).length,
    favourites: points.filter((point) => point.favourite).length,
    never: points.filter(isNeverUsed).length,
    cities: new Set(points.map((point) => point.city)).size,
  }), [points]);

  const visible = useMemo(() => {
    const listQuery = tableQuery.trim().toLowerCase();
    const mapQuery = areaQuery.trim().toLowerCase();
    const filtered = points.filter((point) => {
      if (filter === 'recent' && !isUsedThisWeek(point)) return false;
      if (filter === 'favourite' && !point.favourite) return false;
      if (filter === 'never' && !isNeverUsed(point)) return false;
      if (filter === 'most' && !isMostUsed(point)) return false;
      if (city && point.city !== city) return false;
      if (collectionId && !point.collectionIds.includes(collectionId)) return false;
      const hay = `${point.name} ${point.address} ${point.city} ${point.notes}`.toLowerCase();
      if (listQuery && !hay.includes(listQuery)) return false;
      if (mapQuery && !hay.includes(mapQuery)) return false;
      return true;
    });
    return sortPoints(filtered, sort);
  }, [areaQuery, city, collectionId, filter, points, sort, tableQuery]);

  useEffect(() => {
    if (visible.length === 0) {
      setSelectedId(null);
      return;
    }
    setSelectedId((current) => (
      current && visible.some((point) => point.id === current) ? current : visible[0].id
    ));
  }, [visible]);

  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
      const onPointer = (event: MouseEvent) => {
        const target = event.target as HTMLElement | null;
        if (target?.closest('.sp-menu') || target?.closest('[data-point-menu]')) return;
        setMenu(null);
      };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenu(null);
    };
    document.addEventListener('mousedown', onPointer);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [menu]);

  const selected = points.find((point) => point.id === selectedId) ?? null;
  const menuPoint = points.find((point) => point.id === menu?.pointId) ?? null;
  const activeCollection = COLLECTIONS.find((collection) => collection.id === collectionId) ?? null;

  const openMenu = (pointId: string, source: MenuSource, target: HTMLElement) => {
    if (menu?.pointId === pointId && menu.source === source) {
      setMenu(null);
      return;
    }
    const rect = target.getBoundingClientRect();
    const width = 240;
    const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
    const estimated = 300;
    const openUp = rect.bottom + estimated > window.innerHeight && rect.top > 180;
    const top = openUp ? Math.max(8, rect.top - estimated) : rect.bottom + 6;
    setMenu({ pointId, source, top, left });
  };

  const clearFilters = () => {
    setFilter('all');
    setCity('');
    setCollectionId(null);
    setTableQuery('');
    setAreaQuery('');
    setMoreFilters(false);
  };

  const showAll = () => {
    clearFilters();
    mapRef.current?.fitAll();
  };

  const toggleFavourite = (pointId: string) => {
    const point = points.find((item) => item.id === pointId);
    if (!point) return;
    const favourite = !point.favourite;
    setPoints((current) => current.map((item) => (item.id === pointId ? { ...item, favourite } : item)));
    pushToast(favourite ? `Added ${point.name} to favourites` : `Removed ${point.name} from favourites`);
    setMenu(null);
  };

  const removePoints = (ids: string[]) => {
    const names = points.filter((point) => ids.includes(point.id)).map((point) => point.name);
    setPoints((current) => current.filter((point) => !ids.includes(point.id)));
    setMockingId((current) => (current && ids.includes(current) ? null : current));
    setDialog(null);
    setMenu(null);
    pushToast(names.length === 1 ? `Removed ${names[0]} from history` : `Removed ${names.length} points from history`);
  };

  const runAction = (action: MenuActionId, point: StationaryPoint) => {
    setMenu(null);
    if (action === 'edit-name') setDialog({ type: 'edit-name', pointId: point.id });
    else if (action === 'edit-accuracy') setDialog({ type: 'edit-accuracy', pointId: point.id });
    else if (action === 'toggle-favourite') toggleFavourite(point.id);
    else if (action === 'shortcut') setDialog({ type: 'shortcut', pointId: point.id });
    else if (action === 'remove') setDialog({ type: 'remove', pointIds: [point.id] });
    else if (action === 'share') setDialog({ type: 'share', pointId: point.id });
    else setDialog({ type: 'playlist', pointId: point.id });
  };

  const exportPoints = (list: StationaryPoint[]) => {
    if (list.length === 0) {
      pushToast('Nothing to export');
      return;
    }
    downloadCsv('stationary-points.csv', pointsToCsv(list));
    pushToast(list.length === 1 ? 'Exported 1 point' : `Exported ${list.length} points`);
  };

  const onImport = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseStationaryCsv(text);
      if (parsed.length === 0) {
        pushToast('No valid rows in that CSV');
        return;
      }
      setPoints((current) => [...parsed, ...current]);
      setSelectedId(parsed[0].id);
      setFilter('all');
      pushToast(parsed.length === 1 ? 'Imported 1 point' : `Imported ${parsed.length} points`);
    } catch {
      pushToast('Could not read that CSV');
    }
  };

  const copyCoords = async (point: StationaryPoint) => {
    const copied = await copyText(formatCoords(point.latitude, point.longitude));
    pushToast(copied ? 'Coordinates copied' : 'Could not copy coordinates');
  };

  if (loading) return <StationarySkeleton />;

  return (
    <div className="sp">
      <StationaryBoard
        visible={visible}
        allPoints={points}
        selected={selected}
        counts={counts}
        filter={filter}
        sort={sort}
        layout={layout}
        grouped={grouped}
        tableQuery={tableQuery}
        areaQuery={areaQuery}
        city={city}
        cities={cities}
        activeCollectionName={activeCollection?.name ?? null}
        moreFilters={moreFilters}
        mobilePane={mobilePane}
        mapStyle={mapStyle}
        detailTab={detailTab}
        mockingId={mockingId}
        menu={menu}
        mapRef={mapRef}
        fileRef={fileRef}
        onTableQuery={setTableQuery}
        onAreaQuery={setAreaQuery}
        onFilter={setFilter}
        onSort={setSort}
        onCity={setCity}
        onLayout={setLayout}
        onGrouped={setGrouped}
        onMoreFilters={setMoreFilters}
        onMobilePane={setMobilePane}
        onMapStyle={setMapStyle}
        onDetailTab={setDetailTab}
        onSelect={selectPoint}
        onOpenMenu={openMenu}
        onToggleMock={(point) => {
          if (mockingId === point.id) {
            setMockingId(null);
            pushToast('Mock stopped');
            return;
          }
          setMockingId(point.id);
          pushToast(`Mock started for ${point.name}`);
        }}
        onToggleFavourite={toggleFavourite}
        onEdit={(pointId) => setDialog({ type: 'edit-name', pointId })}
        onCopyCoords={copyCoords}
        onAdd={() => setDialog({ type: 'add' })}
        onImport={onImport}
        onExport={() => exportPoints(points)}
        onCollections={() => setDialog({ type: 'collections' })}
        onClearCollection={() => setCollectionId(null)}
        onShowAll={showAll}
        onClearFilters={clearFilters}
      />

      {menu && menuPoint && (
        <div
          id="stationary-point-menu"
          className="sp-menu"
          role="menu"
          aria-label={`Actions for ${menuPoint.name}`}
          style={{ top: menu.top, left: menu.left }}
        >
          {MENU_ACTIONS.map((action) => {
            const Icon = action.icon;
            const label = action.id === 'toggle-favourite'
              ? (menuPoint.favourite ? 'Remove from favourites' : 'Add to favourites')
              : action.label;
            return (
              <button
                key={action.id}
                type="button"
                role="menuitem"
                className={action.id === 'remove' ? 'is-danger' : ''}
                onClick={() => runAction(action.id, menuPoint)}
              >
                <Icon size={16} aria-hidden="true" />
                {label}
              </button>
            );
          })}
        </div>
      )}

      {dialog && (
        <StationaryDialogs
          dialog={dialog}
          points={points}
          playlists={playlists}
          activeCollectionId={collectionId}
          onClose={closeDialog}
          onToast={pushToast}
          onCreate={(input) => {
            const point = createStationaryPoint({
              id: `point-${Date.now()}`,
              name: input.name,
              address: input.address,
              city: input.city,
              latitude: input.latitude,
              longitude: input.longitude,
              accuracyMeters: input.accuracyMeters,
              notes: input.notes,
              collectionIds: input.collectionId ? [input.collectionId] : [],
              timesUsed: 0,
              lastUsedHours: 0,
              color: 'purple',
            });
            setPoints((current) => [point, ...current]);
            setSelectedId(point.id);
            setFilter('all');
            setCity('');
            setCollectionId(null);
            setTableQuery('');
            setDialog(null);
            pushToast(`Saved ${point.name}`);
          }}
          onSaveName={(pointId, name, notes) => {
            setPoints((current) => current.map((point) => (
              point.id === pointId ? { ...point, name, notes } : point
            )));
            setDialog(null);
            pushToast('Name and notes saved');
          }}
          onSaveAccuracy={(pointId, meters) => {
            setPoints((current) => current.map((point) => (
              point.id === pointId ? { ...point, accuracyMeters: meters } : point
            )));
            setDialog(null);
            pushToast('Accuracy updated');
          }}
          onRemove={removePoints}
          onPickPlaylist={(pointId, playlistId) => {
            const playlist = playlists.find((item) => item.id === playlistId);
            const point = points.find((item) => item.id === pointId);
            if (!playlist || !point) return;
            if (point.playlistIds.includes(playlistId)) {
              pushToast(`This point is already in “${playlist.name}”`);
              return;
            }
            setPoints((current) => current.map((item) => (
              item.id === pointId ? { ...item, playlistIds: [...item.playlistIds, playlistId] } : item
            )));
            setDialog(null);
            pushToast(`Added to “${playlist.name}”`);
          }}
          onCreatePlaylist={(pointId, name) => {
            const id = `playlist-${Date.now()}`;
            setPlaylists((current) => [...current, { id, name }]);
            setPoints((current) => current.map((point) => (
              point.id === pointId ? { ...point, playlistIds: [...point.playlistIds, id] } : point
            )));
            setDialog(null);
            pushToast(`Added to “${name}”`);
          }}
          onPickCollection={(nextId) => {
            setCollectionId((current) => (current === nextId ? null : nextId));
            setDialog(null);
          }}
        />
      )}

      <div className="sp-toasts" aria-live="polite">
        {toasts.map((toast) => (
          <p key={toast.id} className="sp-toast" role="status">{toast.text}</p>
        ))}
      </div>
    </div>
  );
};

export default StationaryView;
